import { AlertCircle, Download01 } from "@untitledui/icons";
import { useRef, useState } from "react";
import ModalBase from "@/components/ui/modal/modal-base";
import { useToast } from "@/providers/toast-provider";
import { ERROR_MAP } from "../constants";
import { useImportDrivers } from "../hooks";
import type { ImportDriverErrorKey, ImportDriversResult } from "../types";
import { FileInput } from "./FileInput";

const BatchRegister = ({
	isOpen,
	onClose,
}: {
	isOpen: boolean;
	onClose: () => void;
}) => {
	const { showToast } = useToast();
	const { mutate, isPending } = useImportDrivers();

	const [file, setFile] = useState<File | null>(null);
	const [importResult, setImportResult] = useState<ImportDriversResult | null>(
		null
	);
	const inputRef = useRef<HTMLInputElement | null>(null);

	const resetState = () => {
		setFile(null);
		setImportResult(null);
		if (inputRef.current) {
			inputRef.current.value = "";
		}
	};

	const collectErrors = (result: ImportDriversResult): string[] => {
		const errorsFound: string[] = [];
		for (const { label, key } of ERROR_MAP) {
			const errors = result[key] ?? [];
			for (const msg of errors) {
				errorsFound.push(`${label}: ${msg}`);
			}
		}
		return errorsFound;
	};

	const handleSubmit = () => {
		if (!file) {
			showToast({
				title: "Nenhum arquivo selecionado",
				description: "Por favor, selecione uma planilha para importar.",
				variant: "error",
			});
			return;
		}

		if (hasValidationErrors()) {
			showToast({
				title: "Existem erros no arquivo",
				description:
					"Corrija os erros listados antes de tentar importar novamente.",
				variant: "error",
			});
			return;
		}

		mutate(file, {
			onSuccess: (result) => {
				setImportResult(result);
				const errorsFound = collectErrors(result);

				if (errorsFound.length > 0) {
					showToast({
						title: "Erros na importação",
						description: errorsFound.join("; "),
						variant: "error",
					});
				} else {
					resetState();
					showToast({
						title: "Motoristas importados com sucesso!",
						variant: "success",
					});
					onClose();
				}
			},
			onError: () => {
				showToast({
					title: "Erro na importação",
					description:
						"Ocorreu um erro ao importar o arquivo. Tente novamente.",
					variant: "error",
				});
			},
		});
	};

	const handleClose = () => {
		resetState();
		onClose();
	};

	const getErrors = (key: ImportDriverErrorKey): string[] =>
		importResult?.[key] ?? [];

	const hasValidationErrors = (): boolean =>
		ERROR_MAP.some(({ key }) => (importResult?.[key]?.length ?? 0) > 0);

	return (
		<ModalBase
			actions={[
				{ label: "Cancelar", color: "secondary", onClick: handleClose },
				{
					label: isPending ? "Enviando..." : "Salvar",
					color: "primary",
					onClick: handleSubmit,
					disabled: !file || isPending || hasValidationErrors(),
				},
			]}
			isOpen={isOpen}
			onClose={handleClose}
			title="Cadastro em lote"
			width={528}
		>
			<div className="mb-5 flex flex-col gap-2.5">
				<p>
					Para cadastrar os motoristas em lote, você precisa preencher a
					planilha disponível abaixo. Preencha as linhas de acordo com a coluna
					demarcada na planilha.
				</p>
				<div className="flex items-center justify-center">
					<a
						className="inline-flex w-max items-center justify-center gap-2.5 rounded-md border border-gray-300 bg-white px-4 py-2 font-medium text-gray-700 text-sm transition-colors hover:bg-gray-50 hover:text-gray-900"
						download
						href="/planilhas/modelo-motorista.xlsx"
					>
						<Download01 className="h-4 w-4 text-gray-600" />
						Baixar planilha modelo
					</a>
				</div>
				<p>
					Para finalizar o cadastro, arraste a planilha para a área indicada
					abaixo.
				</p>

				<FileInput
					mode="excel"
					name="driversBatch"
					onChange={(newFile) => {
						setFile(newFile);
						setImportResult(null);
					}}
					ref={inputRef}
				/>

				{ERROR_MAP.map(({ label, key }) =>
					getErrors(key).map((errMsg) => (
						<li
							className="mb-2 flex items-center gap-2 rounded-md border border-red-300 bg-red-100 px-3 py-2 text-red-700 text-sm"
							key={`${key}-${errMsg}`}
						>
							<AlertCircle className="h-4 w-4 text-red-500" />
							<span className="font-semibold">{label}:</span>
							<span>{errMsg}</span>
						</li>
					))
				)}
			</div>
		</ModalBase>
	);
};

export default BatchRegister;
